<template>
  <div class="container">
    <div class="quota" v-for="(quota, quotaIndex) in quotas" :key="quotaIndex">
      <h3 class="title">
        {{ parseTranslation(QUOTA) }}
      </h3>
      <div>
        <button
          class="plus-button"
          @click="addBtn(quotaIndex)"
          v-if="quota.showBtn && quota.filters.length"
        >
          <img src="@/assets/imgs/plus.svg" alt="Plus" />
        </button>

        <div class="table">
          <div
            class="filters"
            v-for="(filter, filterIndex) in quota.selectedFilters"
            :key="filterIndex"
          >
            <div v-if="filter.subQuestions.length">
              <div :title="filter.name" class="head no-border-right">
                {{ filter.name | maxLength }}

                <button @click="deleteFilter(quotaIndex, filterIndex)">
                  <img src="@/assets/imgs/bin.svg" alt="Bin" />
                </button>
              </div>

              <div class="values no-border-right">
                <div
                  :title="subQuestion.label"
                  v-for="(subQuestion, valueIndex) in filter.subQuestions"
                  :key="valueIndex"
                >
                  {{ subQuestion.label | maxLength }}
                </div>
              </div>
            </div>

            <div>
              <div v-if="!filter.subQuestions.length" :title="filter.name" class="head">
                {{ filter.name | maxLength }}

                <button @click="deleteFilter(quotaIndex, filterIndex)">
                  <img src="@/assets/imgs/bin.svg" alt="Bin" />
                </button>
              </div>

              <div v-else :title="filter.name" class="head border-left"></div>

              <div
                :class="{
                  'border-left': filter.subQuestions.length,
                }"
                class="values"
              >
                <div
                  :title="answer.label"
                  v-for="(answer, valueIndex) in filter.answers"
                  :key="valueIndex"
                >
                  {{ answer.label | maxLength }}
                </div>
              </div>
            </div>
          </div>

          <div class="select-close-filter">
            <select
              class="select-filter"
              v-if="quota.showSelect"
              v-model="quota.selected"
              @change="addFilter(quotaIndex)"
            >
              <option disabled value="choose">
                {{ translations[language][CHOOSE] }}
              </option>

              <option
                :title="filter.name"
                v-for="filter in quota.filters"
                :key="filter.qid"
                :value="filter"
              >
                {{ filter.name | maxLength }}
              </option>
            </select>

            <button
              v-if="quota.showSelect"
              type="button"
              @click="closeSelect(quotaIndex)"
              class="closeSelect"
            >
              <img src="@/assets/imgs/bin.svg" alt="Bin" />
            </button>
          </div>

          <div v-if="quota.selectedFilters.length">
            <div :class="{ 'border-left': quota.showSelect }" class="head">
              {{ parseTranslation(COMPLETE) }}
            </div>

            <div :class="{ 'border-left': quota.showSelect }" class="distribution">
              <div
                class="complete"
                v-for="(complete, completeIndex) in quota.distribution"
                :key="completeIndex"
              >
                <input
                  :disabled="complete.fixed"
                  :min="0"
                  type="number"
                  v-model="complete.value"
                />

                <div>
                  <input
                    :id="`locker-${completeIndex}`"
                    type="checkbox"
                    v-model="complete.fixed"
                  />

                  <label :for="`locker-${completeIndex}`">
                    <img
                      :class="{ active: complete.fixed }"
                      src="@/assets/imgs/locker.svg"
                      alt="Locker"
                    />

                    <img
                      :class="{ active: !complete.fixed }"
                      src="@/assets/imgs/locker-gray.svg"
                      alt="Locker"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div
              class="total"
              :class="{
                textRed: getTotal(quotaIndex) !== wantedCompletes,
                textGreen: getTotal(quotaIndex) === wantedCompletes,
              }"
            >
              {{ getTotal(quotaIndex) }}
            </div>

            <div class="valid-quotas" v-if="getTotal(quotaIndex) !== wantedCompletes">
              {{ parseTranslation(VALIDATION_COMPLETE) }}
            </div>
          </div>
        </div>

        <div class="distribute-button" v-if="quota.selectedFilters.length">
          <button @click="distribute(quotaIndex)">
            {{ parseTranslation(DISTRIBUTE_EQUALLY) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import { UNITARY_SHIFT } from '@/utils/constants'
import { questionsConfig } from '@/utils/questions'

import * as translationQuotaPanel from './QuotaPanel.translations'

const { default: translations, ...translationKeys } = translationQuotaPanel

export default {
  props: {
    survey: {
      required: true,
    },
  },

  filters: {
    maxLength(value) {
      if (value.length > 20) {
        return (
          value
            .split('')
            .splice(0, 20)
            .join('') + '...'
        )
      } else return value
    },
  },

  data() {
    return {
      translations,
      ...translationKeys,
      filters: [],
      filtersTemp: [],
      quotas: [
        {
          selectedFilters: [],
          distribution: [],
          selected: 'choose',
          showSelect: true,
          showBtn: false,
        },
      ],
      completeChecker: false,
    }
  },

  computed: {
    ...mapState(['wantedCompletes']),

    questionMapping() {
      return Object.fromEntries(
        this.survey.questions_groups
          .map(({ questions }) => questions.map(question => [question.code, question]))
          .flat(),
      )
    },

    quotaSections() {
      const sections = this.quotas.map(({ distribution, selectedFilters }) => {
        const [baseFilter, ...filtersToJoin] = selectedFilters

        if (!baseFilter) return { singleQuotas: {}, order: [] }

        const { code } = baseFilter
        const { answers } = this.questionMapping[code]

        const isOWAge = code === 'ows-1'

        const startExpressions = answers.map(
          answer => `${code}.${isOWAge ? answer.label : answer.code}`,
        )

        const allExpressions = this.getQuestionCrossQuotas(
          startExpressions,
          filtersToJoin,
        )

        const singleQuotas = Object.fromEntries(
          allExpressions.map((expression, index) => [
            expression,
            distribution[index].value,
          ]),
        )

        return {
          singleQuotas,
          order: allExpressions,
        }
      })

      console.log(sections)

      return sections
    },
  },

  watch: {
    survey() {
      this.init()
    },

    quotaSections() {
      const { quota, order } = this.quotaSections.reduce(
        (acc, section) => ({
          order: section.order.length ? [...acc.order, section.order] : acc.order,

          quota: {
            ...acc.quota,

            ...section.singleQuotas,
          },
        }),
        { quota: {}, order: [] },
      )

      this.setQuotaJSON(quota)
      this.setQuotaOrder(order)
    },

    completeChecker() {
      this.setCompleteChecker(this.completeChecker)
    },
  },

  methods: {
    ...mapActions([
      'setQuotaOrder',
      'setQuotaJSON',
      'setCompleteChecker',
      'setWantedCompletes',
    ]),

    getQuestionCrossQuotas(accumulatedStrings, remainingQuestions) {
      const [nextJoinerQuestion, ...rest] = remainingQuestions

      if (!nextJoinerQuestion) return accumulatedStrings

      const { code } = nextJoinerQuestion
      const { answers } = this.questionMapping[code]

      const isOWAge = code === 'ows-1'

      const joinedCodes = accumulatedStrings
        .map(quotaString =>
          answers.map(
            answer =>
              `${quotaString} AND ${code}.${isOWAge ? answer.label : answer.code}`,
          ),
        )
        .flat()

      return this.getQuestionCrossQuotas(joinedCodes, rest)
    },

    addBtn(index) {
      this.quotas[index].showSelect = true
      this.quotas[index].showBtn = false
      this.quotas[index].selected = 'choose'
    },

    deleteFilter(quotaIndex, selectedFilterIndex) {
      const { selectedFilters } = this.quotas[quotaIndex]

      const filters = [...selectedFilters]

      const rest = selectedFilters.length - selectedFilterIndex

      selectedFilters.splice(selectedFilterIndex, rest)

      filters.forEach((filter, index) => {
        const filterIndex = this.filters.indexOf(
          this.filters.find(qst => qst.qid === filter.qid),
        )

        if (index >= selectedFilterIndex) {
          this.quotas[quotaIndex].filters.push(this.filters[filterIndex])
        }
      })

      if (selectedFilters.length) {
        const lastIndex = this.quotas[quotaIndex].selectedFilters.length - 1
        const count = this.quotas[quotaIndex].selectedFilters[lastIndex].answers.length

        this.quotas[quotaIndex].distribution = []

        for (let i = 0; i < count; i++) {
          this.quotas[quotaIndex].distribution.push({
            value: 0,
            fixed: false,
          })
        }
      } else {
        this.quotas.splice(quotaIndex, 1)
        this.setQuotaOrder([])
      }

      this.quotas[quotaIndex].showBtn = true
      this.quotas[quotaIndex].showSelect = false
    },

    addFilter(index) {
      const newFilter = {}

      Object.keys(this.quotas[index].selected).forEach(key => {
        if (key !== 'values') newFilter[key] = this.quotas[index].selected[key]
      })

      const arrayFiltersTemp = this.quotas[index].selectedFilters.map(
        filter => filter.qid,
      )

      const unselectedFilters = this.filtersTemp.filter(
        selectedFilter => !arrayFiltersTemp.includes(selectedFilter.qid),
      )

      if (!this.quotas[index].selected.subQuestions.length) {
        const values = this.quotas[index].selected.answers

        if (this.quotas[index].selectedFilters.length >= 1) {
          const previousIndex = this.quotas[index].selectedFilters.length - 1
          const count = this.quotas[index].selectedFilters[previousIndex].answers.length

          this.quotas[index].distribution = []
          newFilter.answers = []

          for (let i = 0; i < count; i++) {
            values.forEach(value => {
              newFilter.answers.push(value)
              this.quotas[index].distribution.push({
                value: 0,
                fixed: false,
              })
            })
          }
        } else {
          newFilter.answers = values

          this.quotas[index].distribution = []

          values.forEach(() => {
            this.quotas[index].distribution.push({
              value: 0,
              fixed: false,
            })
          })
        }

        this.quotas[index].selectedFilters.push(newFilter)
        this.quotas[index].showSelect = false
        this.quotas[index].showBtn = true
        this.quotas[index].selected = 'choose'
        this.quotas[index].filters = [...unselectedFilters]

        if (this.quotas[index].selectedFilters.length === 1) {
          this.quotas.push({
            selectedFilters: [],
            distribution: [],
            selected: 'choose',
            showSelect: true,
            showBtn: false,
            filters: [...this.filtersTemp],
          })
        }
      } else {
        const subQuestions = this.quotas[index].selected.subQuestions
        const answers = this.quotas[index].selected.answers

        if (this.quotas[index].selectedFilters.length >= 1) {
          const previousIndex = this.quotas[index].selectedFilters.length - 1
          const count = this.quotas[index].selectedFilters[previousIndex].answers.length

          newFilter.subQuestions = []

          for (let i = 0; i < count; i++) {
            subQuestions.forEach(value => {
              newFilter.subQuestions.push(value)
            })

            this.quotas[index].distribution = []
            newFilter.answers = []

            for (let i = 0; i < newFilter.subQuestions.length; i++) {
              answers.forEach(value => {
                newFilter.answers.push(value)
                this.quotas[index].distribution.push({
                  value: 0,
                  fixed: false,
                })
              })
            }
          }
        } else {
          newFilter.subQuestions = subQuestions

          this.quotas[index].distribution = []
          newFilter.answers = []

          for (let i = 0; i < newFilter.subQuestions.length; i++) {
            answers.forEach(value => {
              newFilter.answers.push(value)
              this.quotas[index].distribution.push({
                value: 0,
                fixed: false,
              })
            })
          }
        }

        this.quotas[index].selectedFilters.push(newFilter)
        this.quotas[index].showSelect = false
        this.quotas[index].showBtn = true
        this.quotas[index].selected = 'choose'
        this.quotas[index].filters = [...unselectedFilters]

        if (this.quotas[index].selectedFilters.length === 1) {
          this.quotas.push({
            selectedFilters: [],
            distribution: [],
            selected: 'choose',
            showSelect: true,
            showBtn: false,
            filters: [...this.filtersTemp],
          })
        }
      }

      const filterAdded = this.quotas[index].filters.filter(
        filter => filter.qid === newFilter.qid,
      )

      this.quotas[index].filters.splice(
        this.quotas[index].filters.indexOf(filterAdded[0]),
        1,
      )
    },

    closeSelect(index) {
      this.quotas[index].showSelect = false
      this.quotas[index].showBtn = true
    },

    getTotal(quotaIndex) {
      let count = 0

      this.quotas[quotaIndex].distribution.forEach(complete => {
        count += +complete.value
      })

      this.completeChecker = Boolean(
        this.wantedCompletes && count !== this.wantedCompletes,
      )

      return count
    },

    distribute(quotaIndex) {
      let fixedValues = 0
      let fixedCount = 0

      const { distribution } = this.quotas[quotaIndex]

      distribution.forEach(complete => {
        if (complete.fixed) {
          fixedCount += UNITARY_SHIFT
          fixedValues += Number(complete.value)
        }
      })

      const nonFixedQuotas = distribution.filter(complete => !complete.fixed)

      const distributionsWithoutFixed = distribution.length - fixedCount

      const completesWithoutFixedValues = this.wantedCompletes - fixedValues

      let rest = completesWithoutFixedValues % distributionsWithoutFixed

      const distributeValue =
        (completesWithoutFixedValues - rest) / distributionsWithoutFixed

      nonFixedQuotas.forEach(quota => {
        quota.value = distributeValue

        if (!rest) return

        quota.value += UNITARY_SHIFT
        rest -= UNITARY_SHIFT
      })
    },

    init() {
      const questionGroups = this.survey.questions_groups

      const filters = []

      questionGroups.forEach(group => {
        group.questions.forEach(question => {
          if (
            question.type !== 'multiText' &&
            !questionsConfig[question.type].hasSubQuestion &&
            question.answers.length
          ) {
            filters.push({
              qid: question.qid,
              code: question.code,
              name: question.question,
              answers: question.answers,
              subQuestions: question?.subQuestions,
            })
          }

          this.filters = [...filters]
          this.filtersTemp = [...filters]
        })
      })

      this.quotas[0].filters = [...filters]

      if (!this.survey.quota) return

      this.survey.quota_order.forEach((quotaConfig, index) => {
        if (!this.quotas[index])
          this.quotas[index] = {
            selectedFilters: [],
            distribution: [],
            selected: 'choose',
            showSelect: true,
            showBtn: false,
          }

        const [singleQuota] = quotaConfig

        const questionReferences = singleQuota.split(' AND ')

        questionReferences.forEach(reference => {
          const [code] = reference.split('.')

          this.quotas[index].selected = this.filters.find(qst => qst.code === code.trim())

          this.addFilter(index)
        })

        this.quotas[index].distribution.forEach((value, distIndex) => {
          value.value = Number(this.survey.quota[quotaConfig[distIndex]])
        })
      })
    },
  },

  created() {
    this.init()
  },
}
</script>

<style lang="scss" scoped>
.container {
  width: 100% !important;

  .quota {
    margin-top: 60px;
    padding-bottom: 100px;

    > div {
      display: flex;
      align-items: flex-start;
    }
    .plus-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: $blue;
      border-radius: 3px;
      border: none;
      width: 30px;
      height: 30px;
      cursor: pointer;
      animation: show 0.3s linear;
      transition: all 0.3s ease;
      margin-right: 10px;
      &:hover {
        background: $blue-2;
      }
    }
  }

  .title {
    display: flex;
    align-items: center;
    @include tipography(16);
    color: $white;
    font-weight: bold;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    white-space: nowrap;
    margin-bottom: 20px;

    &::after {
      content: '';
      display: block;
      width: 100%;
      height: 1px;
      background: $white;
      margin-left: 15px;
    }
  }

  .table {
    display: flex;
    .filters {
      display: flex;
      &:first-of-type {
        .head {
          border-left: 1px solid $white;
        }

        .values {
          border-left: 1px solid $white;
        }
      }
    }
    .values {
      display: flex;
      flex-direction: column;
      height: 100%;
      @include tipography(12);
      color: $white;
      border-right: 1px solid $white;
      background: $main-3;

      &.no-border-right {
        border-right: none;
      }

      div {
        flex: 1;
        display: flex;
        align-items: center;
        padding: 10px;
        border-bottom: 1px solid $white;

        &:first-of-type {
          border-top: none;
        }
      }
    }
    .head {
      display: flex;
      align-items: center;
      height: 60px;
      background: $main-3;
      border-top: 1px solid $white;
      border-right: 1px solid $white;
      border-bottom: 1px solid $white;

      &.no-border-right {
        border-right: none;
      }

      @include tipography(12);
      color: $white;
      font-weight: bold;
      text-transform: uppercase;
      padding: 10px;
      button {
        margin-left: 10px;
        background: none;
        border: none;
        cursor: pointer;
        transition: all 0.3s ease;
        &:hover {
          transform: scale(1.05);
        }
        &:focus {
          outline: none;
        }
      }
    }
  }

  .border-left {
    border-left: 1px solid $white;
  }

  .select-close-filter {
    display: flex;

    .select-filter {
      animation: show 0.3s linear;
      border: 1px solid $blue;
      background: none;
      border-radius: 3px;
      padding: 5px 15px;
      margin: 0 10px;
      height: 29px;
      max-width: 200px;
      @include tipography(12);
      color: $blue;
      text-transform: uppercase;
      font-weight: bold;
      @include focus;

      &:hover {
        color: $blue-2;
        border-color: $blue-2;
      }

      option {
        color: $main;
        max-width: 200px;
      }
    }

    .closeSelect {
      border: 0.5px solid #0695fd;
      border-radius: 3px;
      height: 29px;
      padding: 5px 10px;
      margin: 0 15px 0 5px;
      background: $main-3;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      &:focus {
        outline: none;
      }
      i {
        color: #0695fd !important;
        font-size: 1.3rem;
      }
    }
  }

  .distribution {
    display: flex;
    flex-direction: column;
    height: 100%;
    border-right: 1px solid $white;
    .complete {
      flex: 1;
      display: flex;
      align-items: center;
      padding: 10px;
      border-bottom: 1px solid $white;
      background: $main-3;
      > input {
        display: flex;
        border: 1px solid $white;
        border-radius: 3px;
        padding: 5px 10px;
        width: 100px;
        margin-right: 10px;
        @include tipography(12);
        color: $white;
        background: $main-3;
        transition: all 0.3s ease;
        &:disabled {
          opacity: 0.3;
        }
        @include focus;
      }
      > div {
        position: relative;
        label {
          img {
            animation: show 0.1s linear;
            display: none;
            &.active {
              display: block;
            }
          }
        }
        input {
          width: 25px;
          height: 25px;
          background: $transparent;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          border: none;
          outline: none;
          position: absolute;
          left: -10px;
          top: -6px;
          cursor: pointer;
        }
      }
    }
  }

  .distribute-button {
    button {
      margin-left: 10px;
      border: 1px solid $blue;
      background: none;
      @include tipography(12);
      color: $blue;
      text-transform: uppercase;
      font-weight: bold;
      border-radius: 3px;
      padding: 5px 15px;
      cursor: pointer;
      transition: all 0.3s ease;
      &:hover {
        color: $blue-2;
        border-color: $blue-2;
      }
    }
  }

  .total {
    text-align: center;
    background: $blue;
    @include tipography(16);
    font-weight: bold;
    padding: 10px;
    &.textRed {
      color: $red;
    }
    &.textGreen {
      color: $green;
      background: $main-3;
      border-right: 1px solid $white;
      border-bottom: 1px solid $white;
      border-left: 1px solid $white;
    }
  }

  .valid-quotas {
    position: absolute;
    margin-top: 5px;
    @include typography1(12, 21, $red, 500);
  }

  .light & {
    .title {
      color: $main;

      &::after {
        background: $gray-5;
      }
    }

    .table {
      .filters {
        &:first-of-type {
          .head {
            border-left: 1px solid $gray-5;
          }
          .values {
            border-left: 1px solid $gray-5;
          }
        }

        .values {
          color: $main;
          border-right: 1px solid $gray-5;
          background: $white;

          div {
            border-bottom: 1px solid $gray-5;
          }
        }
      }
      .head {
        background: $gray-light-3;
        border-top: 1px solid $gray-5;
        border-right: 1px solid $gray-5;
        border-bottom: 1px solid $gray-5;
        color: $gray-7;
      }
    }

    .border-left {
      border-left: 1px solid $gray-5;
    }

    .select-close-filter {
      .closeSelect {
        background: transparent;
      }
    }

    .distribution {
      border-right: 1px solid $gray-5;
      .complete {
        border-bottom: 1px solid $gray-5;
        background: $white;
        > input {
          border: 1px solid $gray-5;
          color: $main;
          background: $white;
        }
      }
    }

    .total {
      &.textGreen {
        color: $green;
        background: $white;
        border-right: 1px solid $gray-5;
        border-bottom: 1px solid $gray-5;
        border-left: 1px solid $gray-5;
      }
    }
  }
}
</style>
